import { useEffect, useRef, useCallback } from 'react'
import { useReduxAuth } from '../../../../app/modules/auth/useReduxAuth'
import { MenuComponent } from '../../../assets/ts/components/MenuComponent'

/**
 * SessionManager - Handles avatar dropdown responsiveness
 * 
 * This component only handles essential avatar dropdown recovery without
 * aggressive menu refreshing that interferes with modals and UI elements.
 */
const SessionManager = () => {
  const { isAuthenticated, user } = useReduxAuth()
  const lastCheckRef = useRef<number>(0)

  // Lightweight avatar dropdown check - no aggressive refreshing
  const checkAvatarDropdown = useCallback(() => {
    const avatarElement = document.querySelector('[data-kt-menu-trigger][data-kt-menu-placement="bottom-end"]')
    if (avatarElement) {
      const menuInstance = MenuComponent.getInstance(avatarElement as HTMLElement)
      
      if (!menuInstance) {
        console.log('⚠️ SessionManager: Avatar dropdown instance missing, will be recreated on next click')
        return false
      }
      
      // Test if the menu can respond to events
      try {
        const canRespond = menuInstance.getElement() && 
                          menuInstance.getTriggerElement() &&
                          typeof menuInstance.show === 'function'
        
        if (!canRespond) {
          console.log('⚠️ SessionManager: Avatar dropdown unresponsive, will be recreated on next click')
          return false
        }
      } catch (error) {
        console.log('⚠️ SessionManager: Avatar dropdown error, will be recreated on next click', error)
        return false
      }
    }
    
    return true
  }, [])

  // Window focus effect - only check avatar responsiveness when user returns after long absence
  useEffect(() => {
    const handleWindowFocus = () => {
      if (isAuthenticated && user) {
        const now = Date.now()
        const timeSinceLastCheck = now - lastCheckRef.current
        
        // Only check if it's been more than 10 minutes since last check
        if (timeSinceLastCheck > 10 * 60 * 1000) {
          console.log('👁️ SessionManager: Window focus after long absence, checking avatar dropdown...')
          setTimeout(() => {
            checkAvatarDropdown()
            lastCheckRef.current = now
          }, 1000)
        }
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [isAuthenticated, user, checkAvatarDropdown])

  // Initialize last check time
  useEffect(() => {
    if (isAuthenticated && user) {
      lastCheckRef.current = Date.now()
    }
  }, [isAuthenticated, user])

  // This component doesn't render anything - it's just for session management
  return null
}

export default SessionManager
